'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import get from 'lodash/get';
import { checkType, formatDateVN } from '@/common/ultils';

// ── Icon giờ nhỏ ──────────────────────────────────────────────
const ClockIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// ── Helper lấy src ảnh ─────────────────────────────────────────
const getImgSrc = (post, deviceType) => checkType(deviceType === 'mobile' ? 'mobile' : 'tablet', post, -1);

// ── Main post (trái, ảnh 16/10 + excerpt) ─────────────────────
const MainPost = ({ post, slug, deviceType, categoryName }) => (
  <Link
    href={`${slug}/${post.slug}`}
    className="block group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
    {/* Thumbnail */}
    <div className="relative w-full overflow-hidden" style={{ aspectRatio: '16/10' }}>
      {post?.imgaeUrls ? (
        <Image
          src={getImgSrc(post, deviceType)}
          alt={post.title}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-300"
        />
      ) : (
        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
          <Image src="/img-default.png" alt="Ảnh mặc định" width={60} height={60} className="object-cover opacity-40" />
        </div>
      )}
      {/* Category badge */}
      {categoryName && (
        <span className="absolute top-3 left-3 bg-[#b71c1c] text-white text-[11px] font-bold px-3 py-0.5 rounded uppercase tracking-wide">
          {categoryName}
        </span>
      )}
    </div>

    {/* Info */}
    <div className="p-4">
      <h3 className="text-[17px] font-bold leading-snug text-gray-800 group-hover:text-[#b71c1c] transition-colors duration-200 line-clamp-3 mb-2">
        {post.title}
      </h3>
      {post.summary && (
        <p className="text-[13.5px] text-gray-500 leading-relaxed line-clamp-2 mb-2" dangerouslySetInnerHTML={{ __html: post.summary }} />
      )}
      {!!(post?.postedDate ?? post?.date) && (
        <span className="text-[12px] text-gray-400 flex items-center gap-1">
          <ClockIcon />
          {formatDateVN(post?.postedDate ?? post?.date)}
        </span>
      )}
    </div>
  </Link>
);

// ── Small post (phải, ảnh nhỏ nằm ngang) ──────────────────────
const SmallPost = ({ post, slug, deviceType }) => (
  <Link href={`${slug}/${post.slug}`} className="flex gap-3 bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow duration-200 group">
    {/* Thumbnail nhỏ */}
    <div className="relative w-[100px] min-w-[100px] h-[76px] rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
      {post?.imgaeUrls ? (
        <Image
          src={getImgSrc(post, deviceType)}
          alt={post.title}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-300"
        />
      ) : (
        <Image src="/img-default.png" alt="Ảnh mặc định" width={40} height={40} className="object-cover opacity-40 m-auto mt-4" />
      )}
    </div>

    {/* Info */}
    <div className="flex flex-col justify-center gap-1">
      <h4 className="text-[14px] font-semibold leading-snug text-gray-800 group-hover:text-[#b71c1c] transition-colors duration-200 line-clamp-2">
        {post.title}
      </h4>
      {!!(post?.postedDate ?? post?.date) && (
        <span className="text-[11px] text-gray-400 flex items-center gap-1">
          <ClockIcon />
          {formatDateVN(post?.postedDate ?? post?.date)}
        </span>
      )}
    </div>
  </Link>
);

// ── Section header ─────────────────────────────────────────────
const SectionHeader = ({ title = 'TIN NỔI BẬT', viewAllHref }) => (
  <div className="flex justify-between items-center mb-5 pb-3 border-b-[3px] border-[#b71c1c]">
    <h2 className="text-[18px] font-extrabold text-[#b71c1c] uppercase flex items-center gap-2">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
      {title}
    </h2>
    {viewAllHref && (
      <a href={viewAllHref} className="text-[13px] text-[#b71c1c] hover:underline flex items-center gap-1 font-medium">
        Xem tất cả
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </a>
    )}
  </div>
);

// ── Main component ─────────────────────────────────────────────
const Activity2Columns = ({ featuredEvents, deviceType, slug = 'tin-tuc', sectionTitle = 'TIN NỔI BẬT', viewAllHref, categoryName }) => {
  const posts = get(featuredEvents, 'results', []);
  const mainPost = posts[0];
  const sidePosts = posts.slice(1, 5); // tối đa 4 bài bên phải

  if (!mainPost) return null;

  return (
    <div className="px-2 md:px-4 py-4">
      <SectionHeader title={sectionTitle} viewAllHref={viewAllHref} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Cột trái – bài chính */}
        <MainPost post={mainPost} slug={slug} deviceType={deviceType} categoryName={categoryName} />

        {/* Cột phải – danh sách nhỏ */}
        {sidePosts.length > 0 && (
          <div className="flex flex-col gap-3">
            {sidePosts.map((post) => (
              <SmallPost key={post.slug} post={post} slug={slug} deviceType={deviceType} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Activity2Columns;
